import React, { useContext, useState, useRef, useCallback } from 'react';
import isFunction from 'lodash/isFunction';
import shortid from 'shortid';
import useLayoutEffect from './useIsomorphicLayoutEffect';
import {
  MultistepApiContext,
  MultistepStateContext,
  MultistepStepContext,
  FormContext,
} from '../Context';

export default function useMultistepStep({ id: userId, relevant }) {
  const relevantRef = useRef();
  const context = useContext(FormContext);
  const [id] = useState(userId ?? shortid.generate());
  const { getRelevance, register, setRelevance, unregister } = useContext(MultistepApiContext);
  const { currentId } = useContext(MultistepStateContext);

  relevantRef.current = relevant;

  const [relevantCallback] = useState(() =>
    // eslint-disable-next-line no-shadow
    (formState) => {
      if (isFunction(relevantRef.current)) {
        return relevantRef.current(formState);
      }

      return true;
    }
  );

  useLayoutEffect(() => {
    const stateSubscription = (formState) => {
      const nextIsRelevant = relevantCallback(formState);

      if (nextIsRelevant !== getRelevance(id)) {
        setRelevance(id, nextIsRelevant);
      }
    };

    const initialIsRelevant = relevantCallback(context.stateRef.current);

    register(id, initialIsRelevant);

    context.subscribe('state', stateSubscription);

    return () => {
      unregister(id);

      context.unsubscribe('state', stateSubscription);
    };
  }, []);

  const render = (children) => {
    const isRelevant = getRelevance(id);
    const isCurrent = currentId === id;

    if (!isCurrent || !isRelevant) {
      return null;
    }

    return (
      <MultistepStepContext.Provider value={{ isMultistepField: true, relevant }}>
        {children}
      </MultistepStepContext.Provider>
    );
  };

  return {
    render,
  };
}
